import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';

/**
 * Save status (section 6.5): Saved / Saving / Not yet backed up / Could not
 * save — always shown inline near the action, and announced politely for
 * screen readers.
 */
export type SaveState = 'idle' | 'saving' | 'saved' | 'pending' | 'error';

export function StatusLine({ state, text }: { state: SaveState; text?: string }) {
  if (state === 'idle' || !text) return null;
  const cls =
    state === 'saved' ? 'status-saved' : state === 'saving' ? 'status-saving' : state === 'pending' ? 'status-pending' : 'status-error';
  return (
    <span className={`status ${cls}`} role="status">
      {state === 'saving' ? <span className="spinner" aria-hidden="true" /> : null}
      {state === 'saved' ? <Icon name="check" /> : null}
      {state === 'pending' ? <Icon name="cloudOff" /> : null}
      {state === 'error' ? <Icon name="warning" /> : null}
      <span>{text}</span>
    </span>
  );
}

/** Screen-reader live region for save, upload and conflict announcements (section 15.3). */
interface AnnouncerValue {
  announce: (message: string, assertive?: boolean) => void;
  toast: (message: string) => void;
}
const AnnouncerContext = createContext<AnnouncerValue | null>(null);

/**
 * Where a toast has to be drawn for anybody to see it.
 *
 * Almost everything in this app opens as a modal `<dialog>` — a story, a
 * profile, a recipe, the editor — and a modal lives in the browser's top layer,
 * above every z-index there is. A toast drawn in the page is therefore drawn
 * *underneath* the very panel whose button was just pressed: "Saved" was being
 * said, faithfully, where nobody could read it. So, like the assistant's
 * spotlight (assistant/Spotlight.tsx), it is drawn inside the dialog that is on
 * top when there is one, and in the page when there is not.
 */
function toastHost(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const dialogs = document.querySelectorAll<HTMLDialogElement>('dialog[open]');
  return dialogs.length ? dialogs[dialogs.length - 1] : document.body;
}

export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');
  const [toasts, setToasts] = useState<Array<{ id: number; text: string }>>([]);
  const nextId = useRef(1);
  /*
   * Looked for again a few times a second for as long as a toast is up, because
   * the dialog on top changes under it: "Shared with the family" is said as the
   * editor closes and the finished recipe opens, and a toast left inside the
   * editor would close with it.
   */
  const [host, setHost] = useState<HTMLElement | null>(null);
  const showing = toasts.length > 0;
  useEffect(() => {
    if (!showing) return undefined;
    // The same element again is no change, so this costs nothing while nothing has moved.
    const look = () => setHost(toastHost());
    look();
    const id = setInterval(look, 200);
    return () => clearInterval(id);
  }, [showing]);

  const announce = useCallback((message: string, isAssertive = false) => {
    if (isAssertive) {
      setAssertive('');
      requestAnimationFrame(() => setAssertive(message));
    } else {
      setPolite('');
      requestAnimationFrame(() => setPolite(message));
    }
  }, []);

  const toast = useCallback(
    (text: string) => {
      const id = nextId.current++;
      setToasts((list) => [...list, { id, text }]);
      announce(text);
      setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), 4200);
    },
    [announce],
  );

  const value = useMemo(() => ({ announce, toast }), [announce, toast]);

  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {polite}
      </div>
      <div className="sr-only" aria-live="assertive" aria-atomic="true">
        {assertive}
      </div>
      {showing &&
        host?.isConnected &&
        createPortal(
          <div className="toast-area">
            {toasts.map((x) => (
              <div className="toast" key={x.id}>
                {x.text}
              </div>
            ))}
          </div>,
          host,
        )}
    </AnnouncerContext.Provider>
  );
}

export function useAnnouncer(): AnnouncerValue {
  const v = useContext(AnnouncerContext);
  return v ?? { announce: () => undefined, toast: () => undefined };
}

/** Hook that manages a save-status lifecycle with automatic reset. */
export function useSaveStatus(resetMs = 3500) {
  const [state, setState] = useState<SaveState>('idle');
  const [text, setText] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const set = useCallback(
    (next: SaveState, message: string, sticky = false) => {
      setState(next);
      setText(message);
      if (timer.current) clearTimeout(timer.current);
      if (!sticky && (next === 'saved' || next === 'pending')) {
        timer.current = setTimeout(() => setState('idle'), resetMs);
      }
    },
    [resetMs],
  );

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return { state, text, set, clear: () => setState('idle') };
}

export function Banner({
  kind = 'info',
  icon,
  title,
  children,
  actions,
}: {
  kind?: 'info' | 'success' | 'warning' | 'error' | 'pending' | 'memory' | 'plain';
  icon?: string;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  const defaultIcon =
    kind === 'warning' ? 'warning' : kind === 'error' ? 'warning' : kind === 'success' ? 'check' : kind === 'memory' ? 'sparkle' : 'info';
  return (
    <div className={`banner ${kind === 'plain' ? '' : `banner-${kind}`}`} role={kind === 'error' ? 'alert' : undefined}>
      <Icon name={icon ?? defaultIcon} />
      <div className="body">
        {title ? <strong>{title}</strong> : null}
        {children}
        {actions ? <div className="actions">{actions}</div> : null}
      </div>
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="empty" role="status">
      <span className="spinner" style={{ width: 24, height: 24, borderWidth: 3, display: 'inline-block', animation: 'spin 0.8s linear infinite', border: '3px solid var(--border-strong)', borderInlineEndColor: 'transparent', borderRadius: '50%' }} />
      {label ? <div className="mt-2">{label}</div> : null}
    </div>
  );
}
